import { ViewportScroller } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router, RouterEvent, Scroll } from '@angular/router';
import { getLocalizedImages, getSelectedStep, getSponsorBanner } from '@app/app.reducer';
import { ScrollToAnchor, ScrollToAnchorDataAttributes } from '@core/data-access/models/scroll-to.model';
import {
  selectActiveExhibitorCompanyTriple,
  selectExhibitorDetails
} from '@core/data-access/store/exhibitor/exhibitor.selectors';
import { Store } from '@ngrx/store';
import { VoucherService } from '@products/services/voucher.service';
import { WINDOW } from '@shared/tokens';
import { WindowSizeService } from '@shared/window-size/window-size.service';
import { has, head, isEmpty, isNil, join } from 'lodash';
import {
  combineLatest,
  debounceTime,
  delay,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  fromEvent,
  map,
  merge,
  Observable,
  ReplaySubject,
  scan,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  timeout,
  timer,
  withLatestFrom
} from 'rxjs';
import { StepRoute } from '../models/routes.model';

@Injectable({
  providedIn: 'root'
})
export class ScrollToService {
  readonly #store = inject(Store);
  readonly #window = inject(WINDOW);
  readonly #router = inject(Router);
  readonly #viewportScroller = inject(ViewportScroller);
  readonly #windowSizeService = inject(WindowSizeService);
  readonly #voucherService = inject(VoucherService);

  #bannerElement: HTMLElement | undefined;
  #sponsorBannerElement: HTMLElement | undefined;
  #topBarElement: HTMLElement | undefined;
  #stepNavigationElement: HTMLElement | undefined;

  #loadedComponentViewSubject$: ReplaySubject<string> = new ReplaySubject(1);

  setBannerElement(bannerElement: HTMLElement) {
    if (!this.#bannerElement?.offsetHeight) {
      this.#bannerElement = bannerElement;
    }

    return {
      initSetScrollToPositionOnStepNavigationChangeSubscription:
        this.initSetScrollToPositionOnStepNavigationChangeSubscription.bind(this)
    };
  }

  setSponsorBannerElement(sponsorBannerElement: HTMLElement) {
    if (!this.#sponsorBannerElement?.offsetHeight) {
      this.#sponsorBannerElement = sponsorBannerElement;
    }

    return {
      scrollToSponsorBanner: this.scrollToSponsorBanner.bind(this)
    };
  }

  setTopBarElement(topBarElement: HTMLElement) {
    if (!this.#topBarElement?.offsetHeight) {
      this.#topBarElement = topBarElement;
    }

    return {
      initSetAnchorScrollToPositionSubscription: this.initSetAnchorScrollToPositionSubscription.bind(this),
      scrollToSponsorBanner: this.scrollToSponsorBanner.bind(this)
    };
  }

  setStepNavigationElement(stepNavigationElement: HTMLElement) {
    if (!this.#stepNavigationElement?.offsetHeight) {
      this.#stepNavigationElement = stepNavigationElement;
    }

    return {
      setTopBarElement: this.setTopBarElement.bind(this),
      initSetAnchorScrollToPositionSubscription: this.initSetAnchorScrollToPositionSubscription.bind(this)
    };
  }

  setLoadedComponentView(loadedComponentView: string) {
    this.#loadedComponentViewSubject$.next(loadedComponentView);
  }

  scrollToFirstInvalidFormField(offsetTop = 170, offsetBottom = 0, timeToScroll = 0.5) {
    const formFieldElementSelectors = [
      'input.ng-invalid',
      'select.ng-invalid',
      'ls-input-text.ng-invalid input',
      'ls-dropdown.ng-invalid',
      '.decorated-checkbox-row.ng-invalid',
      '.decorated-checkbox.ng-invalid',
      '.decorated-checkbox--invalid',
      '.ng-custom-invalid'
    ];

    const invalidFormFields: NodeListOf<HTMLElement> = this.#window.document.querySelectorAll(
      join(formFieldElementSelectors, ', ')
    );

    const firstInvalidFormField = head(invalidFormFields);

    if (!isNil(firstInvalidFormField)) {
      const skipScrollToHiddenFormField = firstInvalidFormField.offsetParent === null;

      if (skipScrollToHiddenFormField) {
        return;
      }

      this.scrollToElement(firstInvalidFormField, offsetTop, offsetBottom, timeToScroll);

      setTimeout(() => firstInvalidFormField.focus(), timeToScroll * 1000);
    }
  }

  scrollToElement(element: HTMLElement, offsetTop: number, offsetBottom: number, animationTime: number) {
    if (isNil(element)) {
      return;
    }

    const { top, bottom } = element.getBoundingClientRect();
    const viewportBottom = this.#windowSizeService.viewportHeight() - offsetBottom;
    const isElementAboveTopFold = top < offsetTop;
    const isElementBelowBottomFold = bottom > viewportBottom;

    if (!isElementAboveTopFold && !isElementBelowBottomFold) {
      return;
    }

    const scrollDistance = isElementAboveTopFold ? top - offsetTop : bottom - viewportBottom;
    const instantScrollAnimationTime = 0.05;

    this.#window.scrollTo({
      top: this.#window.scrollY + scrollDistance,
      behavior: animationTime <= instantScrollAnimationTime ? 'auto' : 'smooth'
    });
  }

  private initSetScrollToPositionOnStepNavigationChangeSubscription(destroyRef: DestroyRef) {
    const synchronousWaitForCurrentStepToLoad = 0;

    const activeExhibitorCompanyTriple$ = this.#store.select(selectActiveExhibitorCompanyTriple);
    const exhibitorDetails$ = this.#store.select(selectExhibitorDetails);

    const isNotSelectedStepFirstLoad$ = this.#store.select(getSelectedStep).pipe(
      scan((steps, step) => [step, ...steps], []),
      filter((steps) => steps.filter((step) => !!step).length > 1)
    );

    const images$ = combineLatest({
      localizedImages: this.#store.select(getLocalizedImages),
      sponsorImages: this.#store.select(getSponsorBanner)
    });

    const isNavigationStarOrEndEvent = (routerEvent: RouterEvent) =>
      routerEvent instanceof NavigationStart || routerEvent instanceof NavigationEnd;

    const isAnchorScrollToTriggered = (routerEvent: RouterEvent) => routerEvent?.url?.includes('#');

    return this.#voucherService
      .getIsRedeemVoucherCodeConfirmed$()
      .pipe(
        distinctUntilChanged(
          (previousIsRedeemVoucherCodeConfirmed, currentIsRedeemVoucherCodeConfirmed) =>
            previousIsRedeemVoucherCodeConfirmed && !currentIsRedeemVoucherCodeConfirmed
        ),
        withLatestFrom(activeExhibitorCompanyTriple$, exhibitorDetails$),
        switchMap(
          ([
            isRedeemVoucherCodeConfirmed,
            beforeVoucherRedeemActiveExhibitorCompanyTriple,
            beforeVoucherRedeemExhibitorDetails
          ]) =>
            combineLatest({
              routerEvent: this.#router.events,
              isNotSelectedStepFirstLoad: isNotSelectedStepFirstLoad$
            }).pipe(
              filter(
                ({ routerEvent }) =>
                  isNavigationStarOrEndEvent(routerEvent as RouterEvent) &&
                  !isAnchorScrollToTriggered(routerEvent as RouterEvent)
              ),
              map(({ routerEvent }) => ({
                isNavigationStartEvent: routerEvent instanceof NavigationStart,
                isNavigationEndEvent: routerEvent instanceof NavigationEnd,
                bannerElement: this.#bannerElement,
                isRedeemVoucherCodeConfirmed,
                beforeVoucherRedeemActiveExhibitorCompanyTriple,
                beforeVoucherRedeemExhibitorDetails
              })),
              filter(({ bannerElement }) => !isEmpty(bannerElement)),
              tap(({ isNavigationStartEvent }) => {
                if (isNavigationStartEvent) {
                  this.#viewportScroller.scrollToPosition([0, 0]);
                }
              }),
              filter(
                ({ isNavigationEndEvent }) =>
                  isNavigationEndEvent && this.#window.history?.state?.redirectFromRoute !== StepRoute.TicketAssignment
              ),
              debounceTime(250),
              withLatestFrom(
                activeExhibitorCompanyTriple$,
                exhibitorDetails$,
                images$,
                (
                  {
                    bannerElement,
                    isRedeemVoucherCodeConfirmed,
                    beforeVoucherRedeemActiveExhibitorCompanyTriple,
                    beforeVoucherRedeemExhibitorDetails
                  },
                  afterVoucherRedeemActiveExhibitorCompanyTriple,
                  afterVoucherRedeemExhibitorDetails,
                  { localizedImages, sponsorImages }
                ) => ({
                  bannerElement,
                  isRedeemVoucherCodeConfirmed,
                  beforeVoucherRedeemActiveExhibitorCompanyTriple,
                  beforeVoucherRedeemExhibitorDetails,
                  afterVoucherRedeemActiveExhibitorCompanyTriple,
                  afterVoucherRedeemExhibitorDetails,
                  isEventBannerVisible: !isEmpty(localizedImages?.banner),
                  isSponsorBannerVisible: !isEmpty(sponsorImages?.sponsorBanner)
                })
              ),
              filter(
                ({ isEventBannerVisible, isSponsorBannerVisible }) => isEventBannerVisible || isSponsorBannerVisible
              ),
              filter(
                ({
                  isRedeemVoucherCodeConfirmed,
                  afterVoucherRedeemActiveExhibitorCompanyTriple,
                  afterVoucherRedeemExhibitorDetails
                }) => {
                  if (!isRedeemVoucherCodeConfirmed) {
                    return true;
                  }

                  if (
                    beforeVoucherRedeemActiveExhibitorCompanyTriple === afterVoucherRedeemActiveExhibitorCompanyTriple
                  ) {
                    return true;
                  }

                  if (has(beforeVoucherRedeemExhibitorDetails, afterVoucherRedeemActiveExhibitorCompanyTriple)) {
                    return true;
                  }

                  return isEmpty(
                    afterVoucherRedeemExhibitorDetails[afterVoucherRedeemActiveExhibitorCompanyTriple]?.banner
                  );
                }
              )
            )
        ),
        delay(synchronousWaitForCurrentStepToLoad),
        takeUntilDestroyed(destroyRef)
      )
      .subscribe(({ bannerElement }) => {
        this.#window.scrollTo({
          top: bannerElement?.offsetHeight,
          behavior: 'smooth'
        });
      });
  }

  private initSetAnchorScrollToPositionSubscription(destroyRef: DestroyRef) {
    const retryFindScrollToElementDelay = 100;
    const maxFindScrollToElementAttempts = 30;
    const waitForLayoutChangesToBatch = 100;
    const waitForLayoutToSettle = 1500;

    const layoutChanges$ = new Observable<void>((subscriber) => {
      const resizeObserver = new ResizeObserver(() => subscriber.next());

      resizeObserver.observe(this.#window.document.body);

      return () => resizeObserver.disconnect();
    });

    const userScrollIntent$ = merge(
      fromEvent(this.#window, 'wheel'),
      fromEvent(this.#window, 'touchstart'),
      fromEvent(this.#window, 'keydown')
    );

    this.#viewportScroller.setOffset(() => [0, this.getStickyHeaderHeight()]);

    const scrollToAnchorDataAttributes: ScrollToAnchorDataAttributes = {
      [ScrollToAnchor.Group]: 'data-group-number',
      [ScrollToAnchor.Package]: 'data-package-number',
      [ScrollToAnchor.Tariff]: 'data-tariff-number'
    };

    const findScrollToElement = (anchor: string): HTMLElement | null => {
      const [anchorType, anchorId] = anchor.split('-');
      const dataAttribute = scrollToAnchorDataAttributes[anchorType as ScrollToAnchor];
      const isAnchorIdANumber = /^\d+$/.test(anchorId);

      if (isNil(dataAttribute) || !isAnchorIdANumber) {
        return null;
      }

      return this.#window.document.querySelector<HTMLElement>(`[${dataAttribute}='${anchorId}']`);
    };

    return this.#router.events
      .pipe(
        filter((routerEvent): routerEvent is Scroll => routerEvent instanceof Scroll && !isEmpty(routerEvent.anchor)),
        map(({ anchor }) => anchor!.toLowerCase()),
        switchMap((anchor) =>
          this.#loadedComponentViewSubject$.pipe(
            first(),
            switchMap(() =>
              timer(0, retryFindScrollToElementDelay).pipe(
                take(maxFindScrollToElementAttempts),
                map(() => findScrollToElement(anchor)),
                first((anchorElement) => !isNil(anchorElement), null)
              )
            ),
            filter((scrollToElement): scrollToElement is HTMLElement => !isNil(scrollToElement)),
            switchMap((scrollToElement) =>
              layoutChanges$.pipe(
                debounceTime(waitForLayoutChangesToBatch),
                startWith(undefined),
                timeout({ each: waitForLayoutToSettle, with: () => EMPTY }),
                takeUntil(userScrollIntent$),
                map(() => scrollToElement)
              )
            )
          )
        ),
        takeUntilDestroyed(destroyRef)
      )
      .subscribe((scrollToElement) => {
        this.#window.scrollTo({
          top: scrollToElement.getBoundingClientRect().top + this.#window.scrollY - this.getStickyHeaderHeight(),
          behavior: 'smooth'
        });
      });
  }

  private getStickyHeaderHeight() {
    const topBarElementHeight = this.#topBarElement?.offsetHeight || 0;
    const stepNavigationElementHeight = this.#stepNavigationElement?.offsetHeight || 0;
    const hasStepNavigationStickyPositionTop =
      !isNil(this.#stepNavigationElement) &&
      getComputedStyle(this.#stepNavigationElement).getPropertyValue('top') !== 'auto';

    return hasStepNavigationStickyPositionTop ? topBarElementHeight + stepNavigationElementHeight : topBarElementHeight;
  }

  private scrollToSponsorBanner() {
    const scrollToElementOffsetTop = this.#sponsorBannerElement?.offsetTop || this.#bannerElement?.offsetHeight || 0;
    const topBarElementHeight = this.#topBarElement?.offsetHeight || 0;
    const spacingBetweenSponsorBannerAndTopBarOnScrollToTop = 10;

    this.#window.scrollTo({
      top: scrollToElementOffsetTop - topBarElementHeight - spacingBetweenSponsorBannerAndTopBarOnScrollToTop,
      behavior: 'smooth'
    });
  }
}
